import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../helpers/axios";
import {
  GET_DOMAIN_MIL_GRAPH,
  GET_DOWNLOAD_STATE_ADMIN_CSV,
  GET_ORG_DOMAIN_GRAPH,
  GET_ORG_LIST,
  GET_ORG_MIL_GRAPH, // Ensure this is correctly imported
} from "../../helpers/url_helpers";
import { downloadBlob } from "../../helpers/utils";
import { showSuccess } from "../../helpers/Notifications/Notification";

// Fetch Organization List
export const fetchOrgList = createAsyncThunk(
  "stateAdminDashboard/fetchOrgList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_ORG_LIST);
      if (!response || !response.data) {
        return rejectWithValue("No response from server");
      }
      if (response.data.error) {
        return rejectWithValue(response.data.error);
      }
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

export const fetchDomainMILGraphData = createAsyncThunk(
  "stateAdminDashboard/fetchDomainMILGraphData",
  async (organizationIds, { rejectWithValue }) => {
    try {
      // Convert the array into query string format
      const response = await api.get(
        `${GET_DOMAIN_MIL_GRAPH}?orgIds=${organizationIds}`
      );
      if (!response || !response.data) {
        return rejectWithValue("No response from server");
      }
      if (response.data.error) {
        return rejectWithValue(response.data.error);
      }

      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

export const fetchOrgMILGraphData = createAsyncThunk(
  "stateAdminDashboard/fetchOrgMILGraphData",
  async ({ orgIds, domainId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_ORG_MIL_GRAPH}?orgIds=${orgIds}`, {
        params: { domainId },
      });

      if (!response || !response.data) {
        return rejectWithValue("No response from server");
      }
      if (response.data.error) {
        return rejectWithValue(response.data.error);
      }
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

// Fetch Org Domain Graph Data
export const fetchOrgDomainGraphData = createAsyncThunk(
  "stateAdminDashboard/fetchOrgDomainGraphData",
  async (organizationIds, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${GET_ORG_DOMAIN_GRAPH}?orgIds=${organizationIds}`
      );
      if (!response || !response.data) {
        return rejectWithValue("No response from server");
      }
      if (response.data.error) {
        return rejectWithValue(response.data.error);
      }
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

// Fetch Org Domain Graph Data
export const downloadCSV = createAsyncThunk(
  "stateAdminDashboard/downloadCSV",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_DOWNLOAD_STATE_ADMIN_CSV}`);
      if (!response || !response.data) {
        return rejectWithValue("No response from server");
      }
      if (response.data.error) {
        return rejectWithValue(response.data.error);
      }
      downloadBlob(response?.data, "Organizations_MIL_Report.csv");
      showSuccess("CSV downloaded successfully");
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

// Initial State
const initialState = {
  orgList: [],
  orgListLoading: false,
  DomainMILGraphData: [],
  DomainMILGraphLoading: false,
  currentPage: 1,
  batchSize: 5,
  OrgMILGraphData: [],
  OrgMILGraphLoading: false,
  OrgDomainGraphData: [],
  OrgDomainGraphLoading: false,
  stateAdminDashboardError: null,
  downloadCSVLoading: false,
};

const stateAdminDashboardSlice = createSlice({
  name: "stateAdminDashboard",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Org List
      .addCase(fetchOrgList.pending, (state) => {
        state.orgListLoading = true;
        state.stateAdminDashboardError = null;
      })
      .addCase(fetchOrgList.fulfilled, (state, action) => {
        state.orgListLoading = false;
        state.orgList =
          action.payload.length > 0
            ? action.payload.map((org) => ({
                id: org.id,
                option: org.name,
                disabled: !org.has_survey,
              }))
            : [];
      })
      .addCase(fetchOrgList.rejected, (state, action) => {
        state.orgListLoading = false;
        state.stateAdminDashboardError = action.payload;
      })

      // Fetch Domain MIL Graph
      .addCase(fetchDomainMILGraphData.pending, (state) => {
        state.DomainMILGraphLoading = true;
        state.stateAdminDashboardError = null;
      })
      .addCase(fetchDomainMILGraphData.fulfilled, (state, action) => {
        state.DomainMILGraphLoading = false;
        state.DomainMILGraphData = action.payload;
        state.currentPage = 1;
      })
      .addCase(fetchDomainMILGraphData.rejected, (state, action) => {
        state.DomainMILGraphLoading = false;
        state.stateAdminDashboardError = action.payload;
      })

      // Fetch Org MIL Graph
      .addCase(fetchOrgMILGraphData.pending, (state) => {
        state.OrgMILGraphLoading = true;
        state.stateAdminDashboardError = null;
      })
      .addCase(fetchOrgMILGraphData.fulfilled, (state, action) => {
        state.OrgMILGraphLoading = false;
        state.OrgMILGraphData = action.payload;
      })
      .addCase(fetchOrgMILGraphData.rejected, (state, action) => {
        state.OrgMILGraphLoading = false;
        state.stateAdminDashboardError = action.payload;
      })

      // Fetch Org Domain Graph
      .addCase(fetchOrgDomainGraphData.pending, (state) => {
        state.OrgDomainGraphLoading = true;
        state.stateAdminDashboardError = null;
      })
      .addCase(fetchOrgDomainGraphData.fulfilled, (state, action) => {
        state.OrgDomainGraphLoading = false;
        state.OrgDomainGraphData = action.payload;
      })
      .addCase(fetchOrgDomainGraphData.rejected, (state, action) => {
        state.OrgDomainGraphLoading = false;
        state.stateAdminDashboardError = action.payload;
      })

      // Download CSV
      .addCase(downloadCSV.pending, (state) => {
        state.downloadCSVLoading = true;
        state.stateAdminDashboardError = null;
      })
      .addCase(downloadCSV.fulfilled, (state, action) => {
        state.downloadCSVLoading = false;
      })
      .addCase(downloadCSV.rejected, (state, action) => {
        state.downloadCSVLoading = false;
        state.stateAdminDashboardError = action.payload;
      });
  },
});

export default stateAdminDashboardSlice.reducer;
export const { setCurrentPage } = stateAdminDashboardSlice.actions;
